import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Check, Phone } from "lucide-react";
import Logo from "@/components/brand/Logo";
import { Button } from "@/ds";
import { FieldError, phoneForServer, phoneOk } from "@/components/auth/SignupParts";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { errorCode, errorMessage } from "@/lib/errors";

/**
 * Confirm the phone, from the dashboard (2026-10-28).
 *
 * Sign-up used to stop here: a FixyBee could not finish until a Twilio code
 * arrived and was typed back, on the same phone that was showing the form.
 * Now sign-up captures the number and this page confirms it, whenever they
 * like: one tap, an SMS, six digits.
 *
 * The code box is one field, not six: `autocomplete="one-time-code"` is what
 * makes iOS offer the code above the keyboard, and it fills one input. A
 * confirmed phone is a mark on the public profile (2026-10-28); it gates
 * nothing but a 100% courtesy code.
 */
export default function PhoneVerify() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const profile = user?.profile || {};
  const [phone, setPhone] = useState(profile.phone || "");
  const [editing, setEditing] = useState(!profile.phone);
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [wait, setWait] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [verified, setVerified] = useState(!!profile.phone_verified);
  const codeRef = useRef(null);

  // The resend countdown, from the number the server gave.
  useEffect(() => {
    if (wait <= 0) return undefined;
    const id = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(id);
  }, [wait]);

  const verify = async (value) => {
    const digits = String(value || "").replace(/\D/g, "");
    if (digits.length < 4) return;
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/verify-otp", { phone: phoneForServer(phone), code: digits });
      setVerified(true);
      await refresh();
    } catch (e) {
      // The server's sentences are English; the status says which one it was.
      setError(e?.response?.status === 400 ? t("onboarding.verify.errVerify")
        : e?.response?.status === 503 ? t("signupX.otpUnavailable")
        : errorMessage(e, t("onboarding.verify.errVerify")));
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    if (!phoneOk(phone)) {
      setError(t("signupX.phoneInvalid"));
      return;
    }
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/send-otp", { phone: phoneForServer(phone) });
      setSent(true);
      setEditing(false);
      setWait(Number(data?.retry_after) || 60);
      setTimeout(() => codeRef.current?.focus(), 50);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (errorCode(e) === "otp_cooldown") {
        setWait(Number(d?.retry_after) || 60);
        setSent(true);
        setError(t("signupX.phoneWait", { s: Number(d?.retry_after) || 60 }));
      } else {
        const status = e?.response?.status;
        setError(status === 400 ? t("signupX.phoneInvalid")
          : status === 503 ? t("signupX.otpUnavailable")
          : t("onboarding.verify.errSend"));
      }
    } finally {
      setBusy(false);
    }
  };

  // No app shell (REG, 2026-10-28): no sidebar, no tab bar, no Dany button —
  // one errand on the screen, like the sign-up it continues. The way back is
  // the link at the top.
  return (
    <div className="min-h-screen bg-cream px-4 py-6 flex justify-center">
      <div className="w-full max-w-md" data-testid="phone-verify-page">
        <div className="mb-6 flex items-center justify-between">
          <Logo size={32} />
          <Link to="/provider/dashboard" className="inline-flex items-center gap-1 min-h-[44px] text-sm font-semibold text-worker underline" data-testid="phone-verify-exit">
            <ArrowLeft size={16} aria-hidden="true" /> {t("signupX.phoneBackToPanel")}
          </Link>
        </div>
        <h1 className="font-display text-3xl font-bold text-worker">{t("onboarding.verify.title")}</h1>

        {verified ? (
          <div className="mt-6 card-base p-5" data-testid="phone-verify-done">
            <p className="flex items-center gap-2 font-display font-semibold text-worker">
              <Check size={20} className="text-success" /> {t("onboarding.verify.verified")}
            </p>
            <p className="mt-2 text-smoke">{t("signupX.phoneDoneBody")}</p>
            <Link to="/provider/dashboard" className="fx-btn fx-btn--primary fx-btn--block mt-5" data-testid="phone-verify-back">
              {t("signupX.phoneBackToPanel")}
            </Link>
          </div>
        ) : (
          <>
            <p className="mt-2 text-smoke">{t("onboarding.verify.subtitle")}</p>

            <div className="mt-6 space-y-5">
              {editing ? (
                <div>
                  <label htmlFor="pv-phone" className="label-base">{t("signupX.phone")}</label>
                  <div className="relative">
                    <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-smoke" aria-hidden="true" />
                    <input
                      id="pv-phone" type="tel" inputMode="tel" autoComplete="tel-national"
                      value={phone} onChange={(e) => setPhone(e.target.value)}
                      className="input-base pl-10 min-h-[48px]" placeholder="(555) 000-0000"
                      data-testid="phone-verify-number-input"
                    />
                  </div>
                </div>
              ) : (
                <div className="card-base p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="label-base">{t("onboarding.verify.numberLabel")}</p>
                    <p className="font-mono text-lg text-worker truncate" data-testid="phone-verify-number">{phone}</p>
                  </div>
                  <button type="button" onClick={() => { setEditing(true); setSent(false); setCode(""); }}
                          className="font-semibold text-worker underline shrink-0 min-h-[44px]" data-testid="phone-verify-change">
                    {t("signupX.phoneChange")}
                  </button>
                </div>
              )}

              {!sent && (
                <Button type="button" fullWidth onClick={send} loading={busy} data-testid="phone-verify-send">
                  {t("onboarding.verify.sendCode")}
                </Button>
              )}

              {sent && (
                <form onSubmit={(e) => { e.preventDefault(); verify(code); }} className="space-y-3">
                  <p className="text-sm text-smoke" data-testid="phone-verify-sent">{t("onboarding.verify.sent")}</p>
                  <div>
                    <label htmlFor="pv-code" className="label-base">{t("onboarding.verify.codeLabel")}</label>
                    <input
                      id="pv-code" ref={codeRef}
                      className="input-base font-mono tracking-[0.5em] text-2xl text-center min-h-[56px]"
                      value={code}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, "").slice(0, 6);
                        setCode(v);
                        // A pasted or autofilled code is complete: check it at once.
                        if (v.length === 6) verify(v);
                      }}
                      autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]*"
                      maxLength={6} placeholder="••••••"
                      data-testid="phone-verify-code"
                    />
                  </div>
                  <Button type="submit" fullWidth loading={busy} disabled={code.length < 4}
                          data-testid="phone-verify-check">
                    {t("onboarding.verify.verify")}
                  </Button>
                  <Button type="button" variant="ghost" fullWidth onClick={send}
                          disabled={busy || wait > 0} data-testid="phone-verify-resend">
                    {wait > 0 ? t("signupX.phoneResendIn", { s: wait }) : t("onboarding.verify.resend")}
                  </Button>
                </form>
              )}

              <FieldError testid="phone-verify-error">{error}</FieldError>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
